import React, { useState, useEffect } from 'react';
import {
  Clock,
  MapPin,
  Phone,
  Mail,
  FileText,
  Camera,
  MessageSquare,
  Star,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  Send,
  User,
  Calendar,
  Euro,
  Zap,
  Edit2,
  Save,
  X,
  Plus,
  Paperclip
} from 'lucide-react';
import { 
  supabase,
  fetchInterventionRequests,
  type InterventionRequest 
} from '../lib/supabase';

interface BookingDetailsProps {
  bookingId: string;
  onBack: () => void;
}

interface Message {
  id: string;
  booking_id: string;
  sender_type: 'customer' | 'professional' | 'admin';
  sender_name: string;
  message: string;
  timestamp: string;
  attachments?: string[];
}

interface StatusUpdate {
  id: string;
  booking_id: string;
  old_status: string;
  new_status: string;
  updated_by: string;
  timestamp: string;
  notes?: string;
}

const statusConfig = {
  scheduled: {
    color: 'bg-blue-100 text-blue-800',
    icon: Calendar,
    label: 'Scheduled'
  },
  in_progress: {
    color: 'bg-yellow-100 text-yellow-800',
    icon: Clock,
    label: 'In Progress'
  },
  completed: {
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
    label: 'Completed'
  },
  cancelled: {
    color: 'bg-red-100 text-red-800',
    icon: X,
    label: 'Cancelled'
  },
  action_needed: {
    color: 'bg-orange-100 text-orange-800',
    icon: AlertCircle,
    label: 'Action Needed'
  }
};

// Mock data for demonstration
const mockMessages: Message[] = [
  {
    id: 'M001',
    booking_id: '',
    sender_type: 'admin',
    sender_name: 'Sarah Johnson',
    message: 'Booking confirmed. Our technician will arrive between 9:00-12:00 AM.',
    timestamp: '2025-01-20T08:30:00Z',
  },
  {
    id: 'M002',
    booking_id: '',
    sender_type: 'professional',
    sender_name: 'Mike Smith',
    message: 'On my way to the location. ETA: 15 minutes.',
    timestamp: '2025-01-20T08:45:00Z',
  },
  {
    id: 'M003',
    booking_id: '',
    sender_type: 'customer',
    sender_name: 'John Doe',
    message: 'Great! I\'ll be waiting at the main entrance.',
    timestamp: '2025-01-20T08:47:00Z',
  }
];

const mockStatusUpdates: StatusUpdate[] = [
  {
    id: 'S001',
    booking_id: '',
    old_status: 'scheduled',
    new_status: 'in_progress',
    updated_by: 'Mike Smith',
    timestamp: '2025-01-20T09:00:00Z',
    notes: 'Technician arrived on site and started work'
  }
];

export function BookingDetails({ bookingId, onBack }: BookingDetailsProps) {
  const [booking, setBooking] = useState<InterventionRequest | null>(null);
  const [messages, setMessages] = useState<Message[]>(mockMessages);
  const [statusUpdates, setStatusUpdates] = useState<StatusUpdate[]>(mockStatusUpdates);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  useEffect(() => {
    loadBookingDetails();
  }, [bookingId]);

  const loadBookingDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch the specific booking
      const { data, error } = await supabase
        .from('intervention_requests')
        .select(`
          *,
          location:locations(*)
        `)
        .eq('id', bookingId)
        .single();

      if (error) {
        throw error;
      }

      setBooking(data);
    } catch (err: any) {
      console.error('Error loading booking details:', err);
      setError('Failed to load booking details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || isSendingMessage) return;

    setIsSendingMessage(true);
    
    try {
      // In a real implementation, this would save to the database
      const message: Message = {
        id: `M${Date.now()}`,
        booking_id: bookingId,
        sender_type: 'admin',
        sender_name: 'Current User',
        message: newMessage.trim(),
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, message]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus || !booking) return;

    try {
      // Update the booking status in the database
      const { error } = await supabase
        .from('intervention_requests')
        .update({ status: newStatus })
        .eq('id', bookingId);

      if (error) throw error;

      // Add status update to timeline
      const statusUpdate: StatusUpdate = {
        id: `S${Date.now()}`,
        booking_id: bookingId,
        old_status: booking.status,
        new_status: newStatus,
        updated_by: 'Current User',
        timestamp: new Date().toISOString(),
        notes: statusNotes.trim() || undefined
      };

      setStatusUpdates(prev => [...prev, statusUpdate]);
      setBooking(prev => prev ? { ...prev, status: newStatus as any } : null);
      setIsEditingStatus(false);
      setNewStatus('');
      setStatusNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update booking status');
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="h-6 w-6 text-red-600" />
          <div>
            <h3 className="text-lg font-semibold text-red-800">Error Loading Booking</h3>
            <p className="text-red-700">{error || 'Booking not found'}</p>
          </div>
        </div>
        <button
          onClick={onBack}
          className="mt-4 gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
        >
          Back to Bookings
        </button>
      </div>
    );
  }

  const StatusIcon = statusConfig[booking.status]?.icon || AlertCircle;
  const statusColor = statusConfig[booking.status]?.color || 'bg-gray-100 text-gray-800';
  const statusLabel = statusConfig[booking.status]?.label || booking.status;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Booking #{booking.id.slice(0, 8)}
            </h1>
            <p className="text-gray-600">{booking.service_title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsEditingStatus(true)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
          >
            <Edit2 className="h-4 w-4" />
            Update Status
          </button>
          <button className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity">
            Download Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                  <StatusIcon className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{statusLabel}</h2>
                  <p className="text-gray-600">
                    {booking.is_urgent ? 'Urgent Request' : 'Standard Request'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                  <StatusIcon className="h-4 w-4 mr-1" />
                  {statusLabel}
                </span>
                {booking.is_urgent && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <Zap className="h-3 w-3 mr-1" />
                    URGENT
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Pricing Information */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pricing Details</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Service Price</span>
                <span className="font-medium">€{booking.service_price?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Handling Fee</span>
                <span className="font-medium">€{booking.handling_fee?.toFixed(2) || '0.00'}</span>
              </div>
              {booking.is_urgent && (
                <div className="flex justify-between items-center text-orange-600">
                  <span>Urgent Surcharge</span>
                  <span className="font-medium">€{((booking.service_price || 0) * 0.5).toFixed(2)}</span>
                </div>
              )}
              <div className="border-t pt-3 flex justify-between items-center text-lg font-bold">
                <span>Total Cost</span>
                <span className="text-jaimy-red">€{booking.total_cost?.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Invoice Status</span>
                <span className={`font-medium ${booking.invoiced ? 'text-green-600' : 'text-orange-600'}`}>
                  {booking.invoiced ? 'Invoiced' : 'Pending Invoice'}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
            <div className="space-y-6">
              {/* Status Updates */}
              {statusUpdates.map((update) => (
                <div key={update.id} className="relative pl-8">
                  <div className="absolute left-0 top-2 h-2 w-2 rounded-full bg-blue-500"></div>
                  <div className="border-l-2 border-gray-200 pl-6 pb-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600">{formatTimestamp(update.timestamp)}</p>
                        <p className="text-gray-900 mt-1">
                          Status changed from <span className="font-medium">{update.old_status}</span> to{' '}
                          <span className="font-medium">{update.new_status}</span>
                        </p>
                        {update.notes && (
                          <p className="text-gray-600 text-sm mt-1">{update.notes}</p>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {update.updated_by}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Initial booking */}
              <div className="relative pl-8">
                <div className="absolute left-0 top-2 h-2 w-2 rounded-full bg-green-500"></div>
                <div className="border-l-2 border-gray-200 pl-6 pb-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{formatTimestamp(booking.created_at)}</p>
                      <p className="text-gray-900 mt-1">Booking created</p>
                      <p className="text-gray-600 text-sm mt-1">
                        {booking.service_title} requested by {booking.contact_person}
                      </p>
                    </div>
                    <div className="text-sm text-gray-500">
                      System
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Messages</h2>
            
            {/* Message List */}
            <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
              {messages.map((message) => (
                <div key={message.id} className="flex gap-3">
                  <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    {message.sender_type === 'customer' ? (
                      <User className="h-4 w-4 text-gray-600" />
                    ) : message.sender_type === 'professional' ? (
                      <Star className="h-4 w-4 text-yellow-600" />
                    ) : (
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-900">{message.sender_name}</span>
                      <span className="text-xs text-gray-500">{formatTimestamp(message.timestamp)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        message.sender_type === 'customer' ? 'bg-blue-100 text-blue-700' :
                        message.sender_type === 'professional' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {message.sender_type}
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm">{message.message}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Message Input */}
            <div className="border-t pt-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent resize-none"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || isSendingMessage}
                    className="gradient-bg text-white p-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSendingMessage ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send className="h-5 w-5" />
                    )}
                  </button>
                  <button className="text-gray-400 hover:text-gray-600 p-2 border border-gray-200 rounded-lg transition-colors">
                    <Paperclip className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <User className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Contact Person</p>
                  <p className="text-gray-900">{booking.contact_person}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <a href={`tel:${booking.contact_phone}`} className="text-gray-900 hover:text-jaimy-red">
                    {booking.contact_phone}
                  </a>
                </div>
              </div>
              {booking.contact_email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <a href={`mailto:${booking.contact_email}`} className="text-gray-900 hover:text-jaimy-red">
                      {booking.contact_email}
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Location Info */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Location</h2>
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="text-gray-900">
                  {booking.location?.name ? (
                    <>
                      <span className="font-medium">{booking.location.name}</span><br />
                      {booking.location.address}
                    </>
                  ) : (
                    booking.manual_address
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Service Details */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Service Details</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600">Service Type</p>
                <p className="text-gray-900 capitalize">{booking.service_type.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Scheduled Time</p>
                <p className="text-gray-900">
                  {booking.selected_time_slot || 'To be determined'}
                </p>
              </div>
              {booking.description && (
                <div>
                  <p className="text-sm text-gray-600">Description</p>
                  <p className="text-gray-900">{booking.description}</p>
                </div>
              )}
              {booking.is_urgent && booking.urgent_reason && (
                <div>
                  <p className="text-sm text-gray-600">Urgent Reason</p>
                  <p className="text-gray-900">{booking.urgent_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {isEditingStatus && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Update Status</h2>
              <button
                onClick={() => setIsEditingStatus(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                >
                  <option value="">Select status</option>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <option key={key} value={key}>
                      {config.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  placeholder="Add any notes about this status change..."
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setIsEditingStatus(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={!newStatus}
                className="gradient-bg text-white px-6 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}